import React from "react";
import SEO from "../../components/common/SEO";
import {
  Shield,
  Lock,
  Globe,
  Key,
  CheckCircle,
  FileCheck,
  Server,
  Users,
  AlertTriangle,
  Mail,
  Sparkles,
} from "lucide-react";

export default function Security({ user, authToken }) {
  return (
    <>
      <SEO
        title="Security & Compliance - VALIDMAIL"
        description="Enterprise-grade security with encryption, data residency options, and GDPR, CCPA, India DPDP compliance. Learn how we protect your data."
        keywords="email validation security, GDPR compliance, data privacy, encryption"
        canonicalUrl="https://validmail.io/company/security"
      />

      <div className="min-h-screen bg-white dark:bg-slate-950 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
              Security & Compliance
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-slate-50 mb-4 sm:mb-6">
              Security-first,{" "}
              <span className="text-emerald-600 dark:text-emerald-400">
                privacy by design
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Enterprise-grade security, global compliance, and transparent data practices. Your data is encrypted, protected, and never shared.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12 md:mb-16">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-emerald-200 dark:border-emerald-800 text-center">
              <Lock className="h-8 w-8 sm:h-10 sm:w-10 text-emerald-600 dark:text-emerald-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">AES-256</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Encryption</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/20 dark:to-sky-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue-200 dark:border-blue-800 text-center">
              <Globe className="h-8 w-8 sm:h-10 sm:w-10 text-blue-600 dark:text-blue-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">3 Regions</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Data Residency</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-purple-200 dark:border-purple-800 text-center">
              <FileCheck className="h-8 w-8 sm:h-10 sm:w-10 text-purple-600 dark:text-purple-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">SOC 2</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Aligned</p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-amber-200 dark:border-amber-800 text-center">
              <Shield className="h-8 w-8 sm:h-10 sm:w-10 text-amber-600 dark:text-amber-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">GDPR</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Compliant</p>
            </div>
          </div>

          <div className="mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-50 text-center mb-8 sm:mb-12">
              Enterprise-grade security
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-emerald-200 dark:border-emerald-800">
                <Lock className="h-10 w-10 sm:h-12 sm:w-12 text-emerald-600 dark:text-emerald-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Encryption Everywhere
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Your data is encrypted both in transit and at rest with industry-standard encryption protocols.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      <strong>TLS 1.3</strong> encryption in transit
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      <strong>AES-256</strong> encryption at rest
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      Encrypted database backups
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/20 dark:to-sky-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-blue-200 dark:border-blue-800">
                <Server className="h-10 w-10 sm:h-12 sm:w-12 text-blue-600 dark:text-blue-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Data Retention Controls
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  We practice data minimization with short default retention windows that you can configure.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      Short default retention windows
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      Configurable retention on higher plans
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      Automatic data deletion after retention period
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-purple-200 dark:border-purple-800">
                <Globe className="h-10 w-10 sm:h-12 sm:w-12 text-purple-600 dark:text-purple-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Data Residency Options
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Choose where your data is stored and processed to meet regional compliance requirements.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      <strong>EU region</strong> for GDPR compliance
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      <strong>India region</strong> for DPDP compliance
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      <strong>US region</strong> for standard hosting
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-amber-200 dark:border-amber-800">
                <Key className="h-10 w-10 sm:h-12 sm:w-12 text-amber-600 dark:text-amber-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Access Controls
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Enterprise-grade access controls and authentication options to protect your account.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      Role-based access control (RBAC)
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      SSO on Scale/Enterprise plans
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      API key rotation and management
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-50 text-center mb-8 sm:mb-12">
              Global compliance
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-slate-200 dark:border-slate-800 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50">
                    GDPR
                  </h3>
                </div>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Full compliance with EU General Data Protection Regulation for data privacy and user rights.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400">•</span>
                    <span>Right to access and deletion</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400">•</span>
                    <span>Data portability</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400">•</span>
                    <span>Consent management</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-slate-200 dark:border-slate-800 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <Shield className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50">
                    CCPA
                  </h3>
                </div>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  California Consumer Privacy Act compliance for US data protection standards.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 dark:text-purple-400">•</span>
                    <span>Consumer data rights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 dark:text-purple-400">•</span>
                    <span>Opt-out of data sales</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 dark:text-purple-400">•</span>
                    <span>Transparent disclosure</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-slate-200 dark:border-slate-800 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <Shield className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50">
                    India DPDP
                  </h3>
                </div>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Aligned with India's Digital Personal Data Protection Act for secure data processing.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400">•</span>
                    <span>Data localization options</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400">•</span>
                    <span>Purpose limitation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400">•</span>
                    <span>Security safeguards</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-50 text-center mb-8 sm:mb-12">
              Why deliverability matters
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-red-200 dark:border-red-800">
                <AlertTriangle className="h-10 w-10 sm:h-12 sm:w-12 text-red-600 dark:text-red-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Understanding Bounces
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Email bounces damage your sender reputation and reduce deliverability. Know the difference:
                </p>
                <div className="space-y-3 text-xs sm:text-sm">
                  <div className="bg-white dark:bg-slate-900 rounded-lg p-3">
                    <p className="font-semibold text-slate-900 dark:text-slate-50 mb-1">Hard Bounces</p>
                    <p className="text-slate-600 dark:text-slate-400">
                      Invalid addresses, non-existent domains. Immediate removal required.
                    </p>
                  </div>
                  <div className="bg-white dark:bg-slate-900 rounded-lg p-3">
                    <p className="font-semibold text-slate-900 dark:text-slate-50 mb-1">Soft Bounces</p>
                    <p className="text-slate-600 dark:text-slate-400">
                      Temporary issues like full mailboxes. May resolve over time.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-yellow-200 dark:border-yellow-800">
                <AlertTriangle className="h-10 w-10 sm:h-12 sm:w-12 text-yellow-600 dark:text-yellow-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Spam Traps & Risk
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Spam traps are email addresses that look valid but are used to catch spammers:
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      Hitting spam traps damages sender reputation
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      Can lead to blacklisting and blocked emails
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      Validation detects and prevents trap hits
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-purple-200 dark:border-purple-800">
                <Mail className="h-10 w-10 sm:h-12 sm:w-12 text-purple-600 dark:text-purple-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Disposable Emails
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Temporary email addresses create poor long-term list quality:
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      Created for one-time use, then abandoned
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      Low engagement rates hurt deliverability
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      Catch them early with validation
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/20 dark:to-sky-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-blue-200 dark:border-blue-800">
                <Users className="h-10 w-10 sm:h-12 sm:w-12 text-blue-600 dark:text-blue-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Abuse & Complainers
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Known complainers trigger spam filters and damage reputation:
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      High spam complaint rates get you blocked
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      ESPs monitor and penalize senders
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">
                      Identify and suppress risky addresses
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-2xl sm:rounded-3xl p-8 sm:p-12 border-2 border-slate-200 dark:border-slate-700 mb-12 md:mb-16">
            <div className="max-w-3xl mx-auto text-center">
              <FileCheck className="h-12 w-12 sm:h-16 sm:w-16 text-emerald-600 dark:text-emerald-400 mx-auto mb-4" />
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-50 mb-4">
                SOC 2 & ISO Alignment
              </h3>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-6">
                Our infrastructure and processes are designed to align with SOC 2 Type II and ISO 27001 best practices for information security management.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 text-sm">
                <div>
                  <div className="text-lg sm:text-xl font-bold text-emerald-600 dark:text-emerald-400 mb-1">
                    Regular Audits
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    Security assessments
                  </p>
                </div>
                <div>
                  <div className="text-lg sm:text-xl font-bold text-emerald-600 dark:text-emerald-400 mb-1">
                    Monitoring
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    24/7 system surveillance
                  </p>
                </div>
                <div>
                  <div className="text-lg sm:text-xl font-bold text-emerald-600 dark:text-emerald-400 mb-1">
                    Incident Response
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    Rapid threat mitigation
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center text-white shadow-2xl">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Questions about security?
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-90 max-w-2xl mx-auto">
              Our security team is here to answer your questions and provide detailed compliance documentation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={() => window.location.href = "/company/contact"}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold bg-white text-emerald-600 hover:bg-slate-50 transition-all shadow-lg hover:shadow-xl"
              >
                Contact Security Team
              </button>
              <button
                onClick={() => window.location.href = "mailto:security@validmail.io"}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold border-2 border-white text-white hover:bg-white hover:text-emerald-600 transition-all"
              >
                Email Security
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
